// The ADMIN-ONLY "Public photos" overlay layer, as plain data so it is
// unit-tested without Mapbox. Mirrors trailHazards/hazardLayerSpec.ts.
//
// Points come from the Martin function source `admin_public_photos` (layer
// `photos`), through the same-origin `/vector` proxy the session cookie
// authenticates. The proxy answers 403 to anyone who isn't an admin who has
// passed the admin two-factor check this session, so hiding the card from
// non-admins (isAdminViewer) is cosmetic: the server is the gate. See
// db/functions/get_admin_public_photos_v1.sql for the tile contract.

import { MapLayerCategory } from '../layer-registry';

export const PUBLIC_PHOTOS_SOURCE_ID = 'admin-public-photos-source';
export const PUBLIC_PHOTOS_LAYER_ID = 'admin-public-photos-layer';

/** Martin source id (the proxy path segment) and the MVT layer inside each tile. */
export const PUBLIC_PHOTOS_MARTIN_SOURCE = 'admin_public_photos';
export const PUBLIC_PHOTOS_SOURCE_LAYER = 'photos';

/** The proxy and the SQL function both refuse tiles below z8 (VectorController::MIN_ZOOM_OVERRIDES). */
export const PUBLIC_PHOTOS_MIN_ZOOM = 8;
/** Tiles exist up to here; Mapbox overzooms past it. */
export const PUBLIC_PHOTOS_MAX_TILE_ZOOM = 16;

/**
 * Above the POI and hazard markers (Top band, z 0 and 1), so a photo stacked on
 * a POI is the thing a click lands on while this admin layer is switched on.
 */
export const PUBLIC_PHOTOS_LAYER_CATEGORY = MapLayerCategory.Top;
export const PUBLIC_PHOTOS_LAYER_Z = 2;

/**
 * Magenta: nothing else on the web map uses it (POIs are icons, hazards are
 * red/orange triangles, recordings lines are #C37098, planned #F57700).
 */
export const PUBLIC_PHOTOS_COLOR = '#E91E8C';

/** How far around a click (px) still counts, so points stacked under it all come along. */
export const PUBLIC_PHOTOS_CLICK_RADIUS = 6;
/** The most ids one click asks the server for (Queries::AdminPublicPhotos::MAX_IDS). */
export const PUBLIC_PHOTOS_MAX_IDS_PER_CLICK = 50;

export const PUBLIC_PHOTOS_PROP_ID = 'id';

/**
 * `{origin}/vector/admin_public_photos/{z}/{x}/{y}`, plus `?t=<version>` once
 * refreshed — the cache-buster that makes Mapbox fetch the tiles again.
 */
export function publicPhotosTileUrl(origin: string, version = 0): string {
  const base = (origin || '').replace(/\/+$/, '');
  const url = `${base}/vector/${PUBLIC_PHOTOS_MARTIN_SOURCE}/{z}/{x}/{y}`;
  return version > 0 ? `${url}?t=${version}` : url;
}

export function publicPhotosSourceSpec(origin: string, version = 0) {
  return {
    type: 'vector' as const,
    tiles: [publicPhotosTileUrl(origin, version)],
    minzoom: PUBLIC_PHOTOS_MIN_ZOOM,
    maxzoom: PUBLIC_PHOTOS_MAX_TILE_ZOOM,
  };
}

/**
 * One circle layer. Vector tiles can't be clustered (Mapbox clusters GeoJSON
 * sources only) and no web overlay has a vector density treatment to follow, so
 * low zoom is handled by size and opacity instead: small, translucent dots at
 * z8–10 read as density, growing into distinct, outlined, clickable dots.
 */
export function publicPhotosLayerSpec() {
  return {
    id: PUBLIC_PHOTOS_LAYER_ID,
    type: 'circle' as const,
    source: PUBLIC_PHOTOS_SOURCE_ID,
    'source-layer': PUBLIC_PHOTOS_SOURCE_LAYER,
    minzoom: PUBLIC_PHOTOS_MIN_ZOOM,
    paint: {
      'circle-color': PUBLIC_PHOTOS_COLOR,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 2, 11, 3.5, 14, 6, 17, 8],
      'circle-opacity': ['interpolate', ['linear'], ['zoom'], 8, 0.55, 12, 0.9],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 11, 1, 14, 1.5],
    },
  };
}

/**
 * The photo id a tile feature carries, as a string — Martin encodes it as a
 * string, some encoders as a number. Null when there isn't a usable one.
 */
export function publicPhotoIdFromFeature(feature: any): string | null {
  const raw = feature?.properties?.[PUBLIC_PHOTOS_PROP_ID];
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(Math.trunc(raw)) : null;
  if (typeof raw !== 'string') return null;
  const id = raw.trim();
  return /^\d+$/.test(id) ? id : null;
}

/**
 * The distinct photo ids among features, in the order Mapbox returned them
 * (top-most first), capped at what one request may ask for. A point on a tile
 * edge is drawn in both tiles, so duplicates are expected.
 */
export function uniquePhotoIds(features: any[] | null | undefined, max = PUBLIC_PHOTOS_MAX_IDS_PER_CLICK): string[] {
  const ids: string[] = [];
  for (const feature of features || []) {
    const id = publicPhotoIdFromFeature(feature);
    if (id && !ids.includes(id)) ids.push(id);
    if (ids.length >= max) break;
  }
  return ids;
}

interface PointLike { x: number; y: number }

/** The box `radius` px around a click, as queryRenderedFeatures takes it. */
export function clickBox(point: PointLike, radius = PUBLIC_PHOTOS_CLICK_RADIUS): [[number, number], [number, number]] {
  return [[point.x - radius, point.y - radius], [point.x + radius, point.y + radius]];
}

interface PhotoHitMap {
  getLayer(id: string): unknown;
  queryRenderedFeatures(geometry: unknown, options: { layers: string[] }): unknown[];
}

/** Every photo id within a few px of a click — the stacked points come along. */
export function photoIdsAtPoint(map: PhotoHitMap | null | undefined, point: PointLike): string[] {
  if (!map || !map.getLayer(PUBLIC_PHOTOS_LAYER_ID)) return [];
  return uniquePhotoIds(map.queryRenderedFeatures(clickBox(point), { layers: [PUBLIC_PHOTOS_LAYER_ID] }));
}

/**
 * Whether a click landed on a photo point — the same box the layer's own click
 * uses. For the map's other click handlers (POIs, overlay lines) to step aside,
 * so one click opens one card.
 */
export function clickLandsOnPublicPhoto(map: PhotoHitMap | null | undefined, point: PointLike | null | undefined): boolean {
  if (!point) return false;
  return photoIdsAtPoint(map, point).length > 0;
}

/**
 * Whether the viewer is an admin, for showing the overlay's card at all.
 * `window.user.admin` is a boolean (User#as_json, and /user/current which
 * authSync swaps in); `window.admin` is the layout's STRING 'true' / 'false' —
 * and 'false' is truthy, so it is compared, never just tested. Signed out
 * (no `window.user`, e.g. after authSync's sign-out) is never an admin, whatever
 * the page was rendered with.
 */
export function isAdminViewer(user: any, adminFlag: unknown): boolean {
  if (!user) return false;
  if (user.admin === true || user.admin === 'true') return true;
  if (user.admin === false || user.admin === 'false') return false;
  return adminFlag === true || adminFlag === 'true';
}
